import React, { useState, useEffect, useRef } from "react";
import config from "../config";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const hoverTimeoutRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleMouseEnter = (name) => {
    clearTimeout(hoverTimeoutRef.current);
    setActiveDropdown(name);
  };

  const handleMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => setActiveDropdown(null), 150);
  };

  // Function to handle smooth scrolling
  const handleNavClick = (e, targetId) => {
    e.preventDefault();
    const element = document.getElementById(targetId);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
    // Close mobile menu if open
    setIsMobileMenuOpen(false);
  };

  // Navigation items matching your sections
  const navItems = [
    { name: "Home", id: "home" },
    { name: "Why Choose Us", id: "why-cfo" },
    { name: "Our Experts", id: "team" },
    { name: "Our Services", id: "services" },
    { name: "Testimonials", id: "testimonials" },
  ];

  const locationItems = [
    { name: "USA", route: "/", flagCode: "us" },
    { name: "India", route: "https://cfobridge.com/", flagCode: "in" },
    { name: "UAE", route: "https://cfobridge.ae", flagCode: "ae" },
  ];

  return (
    <>
      {/* Main Navigation */}
      <nav className={`main-nav ${isScrolled ? "sticky" : ""}`}>
        <div className="inner-nav">
          {/* Mobile Menu Button - Left */}
          <button
            className={`menu-btn ${isMobileMenuOpen ? "active" : ""}`}
            onClick={toggleMobileMenu}
          >
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
          </button>

          {/* Logo - Center on mobile, Left on desktop */}
          <div className="l-part">
            <a
              href="#home"
              className="logo"
              onClick={(e) => handleNavClick(e, "home")}
            >
              <img
                src={`${config.imageBaseURL}${config.paths.img}logo.png`}
                alt="CFO Bridge UAE"
              />
            </a>
          </div>

          {/* Desktop Navigation - Center */}
          <div className="m-part">
            <ul className="nav-list">
              {navItems.map((item) => (
                <li key={item.id}>
                  <a
                    href={`#${item.id}`}
                    onClick={(e) => handleNavClick(e, item.id)}
                  >
                    {item.name}
                  </a>
                </li>
              ))}
              {/* Locations */}
              <li>
                <div
                  className={`nav-dropdown-wrapper ${activeDropdown === "locations" ? "open" : ""}`}
                  onMouseEnter={() => handleMouseEnter("locations")}
                  onMouseLeave={handleMouseLeave}
                >
                  <span
                    className="nav-dropdown-trigger"
                    onClick={() =>
                      setActiveDropdown(
                        activeDropdown === "locations" ? null : "locations",
                      )
                    }
                  >
                    Locations
                    <svg className="dd-chevron" viewBox="0 0 10 6" fill="none">
                      <path
                        d="M1 1L5 5L9 1"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                  <div
                    className="dd-panel"
                    onMouseEnter={() => handleMouseEnter("locations")}
                    onMouseLeave={handleMouseLeave}
                  >
                    {locationItems.map((loc, i) => (
                      <React.Fragment key={loc.name}>
                        <a
                          href={loc.route}
                          className="dd-item"
                          onClick={(e) => {
                            e.preventDefault();
                            setActiveDropdown(null);
                            window.location.href = loc.route;
                          }}
                        >
                          <span
                            className={`fi fi-${loc.flagCode} dd-flag`}
                          ></span>
                          {loc.name}
                        </a>
                        {i < locationItems.length - 1 && (
                          <div className="dd-divider" />
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </li>
            </ul>
          </div>

          {/* Contact Button - Right */}
          <div className="r-part">
            <div className="navbar-actions">
              <a
                href="#contact"
                className="btn-contact"
                onClick={(e) => handleNavClick(e, "contact")}
              >
                <span className="btn-text">Get In Touch</span>
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Navigation */}
      <div className={`mobile-nav ${isMobileMenuOpen ? "active" : ""}`}>
        <div className="menu-head">
          <a
            href="#home"
            className="logo"
            onClick={(e) => handleNavClick(e, "home")}
          >
            <img
              src={`${config.imageBaseURL}${config.paths.img}logo.png`}
              alt="CFO Bridge UAE"
            />
          </a>
          <button className="menu-close" onClick={toggleMobileMenu}>
            <span className="close-line"></span>
            <span className="close-line"></span>
          </button>
        </div>

        <ul className="nav-list">
          {navItems.map((item) => (
            <li key={item.id}>
              <a
                href={`#${item.id}`}
                onClick={(e) => handleNavClick(e, item.id)}
              >
                <span>{item.name}</span>
                <span className="nav-arrow">→</span>
              </a>
            </li>
          ))}

          {/* Locations */}
          <li>
            <div
              className={`mobile-dropdown-trigger ${activeDropdown === "mob-loc" ? "open" : ""}`}
              onClick={() =>
                setActiveDropdown(
                  activeDropdown === "mob-loc" ? null : "mob-loc",
                )
              }
            >
              <span>Locations</span>
              <span className="mobile-dd-chevron">▼</span>
            </div>
            <div
              className={`mobile-dropdown-items ${activeDropdown === "mob-loc" ? "open" : ""}`}
            >
              {locationItems.map((loc) => (
                <a
                  key={loc.name}
                  href={loc.route}
                  className="mobile-dd-item"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsMobileMenuOpen(false);
                    setActiveDropdown(null);
                    window.location.href = loc.route;
                  }}
                >
                  <span
                    className={`fi fi-${loc.flagCode}`}
                    style={{ marginRight: "8px" }}
                  ></span>
                  {loc.name}
                </a>
              ))}
            </div>
          </li>

          <li className="mobile-contact">
            <a href="#contact" onClick={(e) => handleNavClick(e, "contact")}>
              <span>Contact Us</span>
              <span className="nav-arrow">→</span>
            </a>
          </li>
        </ul>
        {/* 
        <div className="mobile-contact-info">
          <div className="mobile-contact-item">
            <span className="contact-icon">✉</span>
            <a href="mailto:info@cfobridge.ae">info@cfobridge.ae</a>
          </div>
          <div className="mobile-contact-item">
            <span className="contact-icon">📞</span>
            <a href="tel:+971-4-xxx-xxxx">+971 4 XXX XXXX</a>
          </div>
        </div>*/}
      </div>
    </>
  );
};

export default Navbar;
